import { useState } from 'react'
import clsx from 'clsx'
import { TrendingUp, TrendingDown, Minus, Info, Target, AlertTriangle } from 'lucide-react'

interface MetricCardProps {
  label: string
  value: string | number
  unit?: string
  trend?: 'up' | 'down' | 'stable'
  trendValue?: number
  status?: 'healthy' | 'warning' | 'critical' | 'neutral'
  size?: 'sm' | 'md' | 'lg'
  tooltip?: string
  // Enhanced properties for SME/Technician context
  description?: string  // Detailed explanation of what this metric means
  target?: number | string  // Target/threshold value
  targetLabel?: string  // Label for the target (e.g., "Target", "Threshold", "Limit")
  showStatusIndicator?: boolean  // Show a visual status indicator bar
  helpText?: string  // Quick help text for technicians
  className?: string
}

export default function MetricCard({
  label,
  value,
  unit,
  trend,
  trendValue,
  status = 'neutral',
  size = 'md',
  tooltip,
  description,
  target,
  targetLabel = 'Target',
  showStatusIndicator = false,
  helpText,
  className
}: MetricCardProps) {
  const [showHelp, setShowHelp] = useState(false)

  const statusColors = {
    healthy: 'text-status-healthy',
    warning: 'text-status-warning',
    critical: 'text-status-critical',
    neutral: 'text-surface-200'
  }

  const statusBgColors = {
    healthy: 'bg-status-healthy',
    warning: 'bg-status-warning',
    critical: 'bg-status-critical',
    neutral: 'bg-surface-500'
  }

  const trendColors = {
    up: 'text-status-healthy',
    down: 'text-status-critical',
    stable: 'text-surface-400'
  }

  const TrendIcon = {
    up: TrendingUp,
    down: TrendingDown,
    stable: Minus
  }

  const sizes = {
    sm: { value: 'text-lg', label: 'text-xs' },
    md: { value: 'text-2xl', label: 'text-xs' },
    lg: { value: 'text-3xl', label: 'text-sm' }
  }

  // Calculate progress percentage if target is numeric
  const progressPercent = target && typeof value === 'number' && typeof target === 'number'
    ? Math.min(100, Math.max(0, (value / target) * 100))
    : null

  return (
    <div
      className={clsx(
        'bg-surface-800 border border-surface-700 rounded-lg p-4 relative group',
        status === 'critical' && 'border-status-critical/30',
        status === 'warning' && 'border-status-warning/30',
        className
      )}
      title={tooltip}
    >
      {/* Header with label and info icon */}
      <div className="flex items-center justify-between mb-1">
        <p className={clsx('metric-label', sizes[size].label)}>{label}</p>
        {(description || helpText) && (
          <button
            className="text-surface-500 hover:text-surface-300 transition-colors p-0.5"
            onClick={() => setShowHelp(!showHelp)}
            title="Click for more information"
          >
            <Info size={14} />
          </button>
        )}
      </div>

      {/* Help/Description popup */}
      {showHelp && (description || helpText) && (
        <div className="absolute z-10 top-full left-0 right-0 mt-1 p-3 bg-surface-900 border border-surface-600 rounded-lg shadow-xl">
          {description && (
            <p className="text-xs text-surface-300 mb-2">{description}</p>
          )}
          {helpText && (
            <div className="flex items-start gap-2 text-xs text-surface-400 bg-surface-800 p-2 rounded">
              <AlertTriangle size={12} className="text-status-warning mt-0.5 flex-shrink-0" />
              <span>{helpText}</span>
            </div>
          )}
        </div>
      )}

      {/* Value display */}
      <div className="flex items-baseline gap-2">
        <span className={clsx('font-mono font-semibold', sizes[size].value, statusColors[status])}>
          {value}
        </span>
        {unit && (
          <span className="text-sm text-surface-500">{unit}</span>
        )}
      </div>

      {/* Target indicator */}
      {target && (
        <div className="flex items-center gap-1.5 mt-1.5">
          <Target size={12} className="text-surface-500" />
          <span className="text-xs text-surface-500">
            {targetLabel}: {target}{unit ? ` ${unit}` : ''}
          </span>
        </div>
      )}

      {/* Status indicator bar */}
      {showStatusIndicator && progressPercent !== null && (
        <div className="mt-2">
          <div className="h-1 bg-surface-700 rounded-full overflow-hidden">
            <div
              className={clsx('h-full transition-all', statusBgColors[status])}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* Trend indicator */}
      {trend && (() => {
        const IconComponent = TrendIcon[trend]
        return (
          <div className={clsx('flex items-center gap-1 mt-2', trendColors[trend])}>
            {IconComponent && <IconComponent size={14} />}
            {trendValue !== undefined && (
              <span className="text-xs font-medium">
                {trend === 'up' ? '+' : trend === 'down' ? '-' : ''}
                {Math.abs(trendValue)}%
              </span>
            )}
            <span className="text-xs text-surface-500 ml-1">
              {trend === 'up' ? 'increase' : trend === 'down' ? 'decrease' : 'no change'}
            </span>
          </div>
        )
      })()}

      {/* Status corner indicator for critical/warning states */}
      {(status === 'critical' || status === 'warning') && (
        <div className={clsx(
          'absolute top-0 right-0 w-2 h-2 rounded-bl-lg',
          statusBgColors[status]
        )} />
      )}
    </div>
  )
}
